import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, Tv, Star, Trophy, BarChart2, Clock, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_META = {
  netflix:        { name: 'Netflix',      color: '#E50914' },
  hulu:           { name: 'Hulu',         color: '#1CE783' },
  disney_plus:    { name: 'Disney+',      color: '#113CCF' },
  hbo_max:        { name: 'Max',          color: '#B535F6' },
  amazon_prime:   { name: 'Prime Video',  color: '#00A8E1' },
  apple_tv:       { name: 'Apple TV+',    color: '#aaaaaa' },
  peacock:        { name: 'Peacock',      color: '#FDB927' },
  paramount_plus: { name: 'Paramount+',   color: '#0064FF' },
};

const DNA_COLORS = {
  'Completionist': 'text-emerald-400',
  'Explorer':      'text-violet-400',
  'Binge Watcher': 'text-[#4A9EFF]',
  'Selective':     'text-yellow-400',
  'Casual Viewer': 'text-neutral-400',
};

export default function Profile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        setError(false);
        const token = await getToken();
        const res = await axios.get(`${API}/users/${username}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setProfile(res.data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    if (username) fetchProfile();
  }, [username, getToken]);

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="max-w-2xl mx-auto py-24 text-center">
        <Users className="w-12 h-12 text-neutral-700 mx-auto mb-4" />
        <p className="text-neutral-500 mb-4">User not found.</p>
        <button onClick={() => navigate(-1)} className="text-violet-400 text-sm hover:text-violet-300 transition-colors">
          ← Go back
        </button>
      </div>
    );
  }

  const initial = (profile.name || profile.username || '?')[0].toUpperCase();

  return (
    <div className="max-w-2xl mx-auto pb-12">
      <button onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-neutral-400 hover:text-white transition-colors mb-6 mt-1">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      {/* Profile header */}
      <div className="flex items-center gap-5 mb-8">
        <div className="w-20 h-20 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden border-2 border-white/10"
          style={{ background: 'linear-gradient(135deg,#7c3aed,#4A9EFF)' }}>
          {profile.picture
            ? <img src={profile.picture} alt={profile.name} className="w-full h-full object-cover" />
            : <span className="text-2xl font-black text-white">{initial}</span>
          }
        </div>
        <div>
          <h1 className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit' }}>
            {profile.name || profile.username}
          </h1>
          <p className="text-sm text-neutral-500">@{profile.username}</p>
          {profile.member_since && (
            <p className="text-xs text-neutral-600 mt-1">Member since {profile.member_since}</p>
          )}
          {profile.dna_label && (
            <span className={`text-xs font-bold mt-1 inline-block ${DNA_COLORS[profile.dna_label] || 'text-neutral-400'}`}>
              {profile.dna_label}
            </span>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        <div className="rounded-2xl bg-[#0A0A0A] border border-white/5 p-4 text-center">
          <p className="text-2xl font-black text-white" style={{ fontFamily: 'Outfit' }}>{profile.total_shows}</p>
          <p className="text-xs text-neutral-500 mt-1">Total Shows</p>
        </div>
        <div className="rounded-2xl bg-[#0A0A0A] border border-white/5 p-4 text-center">
          <p className="text-2xl font-black text-emerald-400" style={{ fontFamily: 'Outfit' }}>{profile.watched}</p>
          <p className="text-xs text-neutral-500 mt-1">Finished</p>
        </div>
        <div className="rounded-2xl bg-[#0A0A0A] border border-white/5 p-4 text-center">
          <p className="text-2xl font-black text-[#4A9EFF]" style={{ fontFamily: 'Outfit' }}>{profile.watching}</p>
          <p className="text-xs text-neutral-500 mt-1">Watching</p>
        </div>
      </div>

      {/* Completion rate */}
      {profile.completion_rate !== undefined && (
        <div className="mb-8 p-4 rounded-2xl bg-[#0A0A0A] border border-white/5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-orange-400" />
              <p className="text-sm font-semibold">Completion Rate</p>
            </div>
            <span className={`text-sm font-black ${
              profile.completion_rate >= 80 ? 'text-emerald-400' :
              profile.completion_rate >= 50 ? 'text-yellow-400' : 'text-red-400'
            }`}>{profile.completion_rate}%</span>
          </div>
          <div className="w-full h-2 rounded-full bg-white/5 overflow-hidden">
            <div className={`h-full rounded-full transition-all ${
              profile.completion_rate >= 80 ? 'bg-emerald-400' :
              profile.completion_rate >= 50 ? 'bg-yellow-400' : 'bg-red-400'
            }`} style={{ width: `${profile.completion_rate}%` }} />
          </div>
        </div>
      )}

      {/* Currently watching */}
      {profile.currently_watching?.length > 0 && (
        <div className="mb-8">
          <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-3">Currently Watching</p>
          <div className="flex gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'none' }}>
            {profile.currently_watching.map(show => (
              <Link
                key={show.show_id}
                to={show.tmdb_id ? `/show/${show.tmdb_id}` : '#'}
                className="flex-shrink-0 w-24 no-underline group"
              >
                <div className="w-24 h-36 rounded-xl bg-white/5 overflow-hidden mb-1.5 relative">
                  {show.image_url
                    ? <img src={show.image_url} alt={show.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                    : <div className="w-full h-full flex items-center justify-center"><Tv className="w-6 h-6 text-neutral-600" /></div>
                  }
                  {show.current_season && show.current_episode && (
                    <div className="absolute bottom-1.5 left-1.5 bg-black/70 text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                      S{show.current_season}E{show.current_episode}
                    </div>
                  )}
                </div>
                <p className="text-xs text-white font-semibold truncate">{show.title}</p>
                {show.service_id && SERVICE_META[show.service_id] && (
                  <p className="text-[10px]" style={{ color: SERVICE_META[show.service_id].color }}>
                    {SERVICE_META[show.service_id].name}
                  </p>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Top rated */}
      {profile.top_rated?.length > 0 && (
        <div className="mb-8">
          <p className="text-xs font-black tracking-widest text-neutral-500 uppercase mb-3">Top Rated</p>
          <div className="space-y-2">
            {profile.top_rated.map((item, i) => (
              <Link
                key={i}
                to={item.tmdb_id ? `/show/${item.tmdb_id}` : '#'}
                state={item.tmdb_id ? { media_type: item.media_type || 'tv' } : undefined}
                className="flex items-center gap-3 p-3 rounded-xl bg-[#0A0A0A] border border-white/5 hover:border-violet-500/30 transition-all no-underline"
              >
                <span className="text-xs font-mono text-neutral-600 w-4 text-center flex-shrink-0">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate text-white">{item.title}</p>
                  {item.review && (
                    <p className="text-xs text-neutral-500 truncate italic">"{item.review}"</p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {[1,2,3,4,5].map(n => (
                    <Star key={n} className={`w-3 h-3 ${n <= item.rating ? 'fill-yellow-400 text-yellow-400' : 'text-neutral-700'}`} />
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
